import React, { useState, useMemo, useCallback, useEffect } from "react";
import { Box, Paper, Typography, Grid, Button, IconButton, Stack, alpha, useTheme, Alert, CircularProgress, Divider } from "@mui/material";
import { Add as AddIcon, Delete as DeleteIcon, Hotel as HotelIcon } from "@mui/icons-material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import i18next from "i18next";
import FormInput from "../../inputs/FormInput";
import useGetProductsForProgram from "../../../hooks/products/useGetProductsForProgram";
import useGetCountriesForSelect from "../../../hooks/countries/useGetCountriesForSelect";
import useGetCitiesForSelect from "../../../hooks/cities/useGetCitiesForSelect";
import useGetHotelMealPlansForSelect from "../../../hooks/hotelMealPlans/useGetHotelMealPlansForSelect";
import useGetHotelRatingsForSelect from "../../../hooks/hotelRatings/useGetHotelRatingsForSelect";
import useGetHotelRoomCategoriesForSelect from "../../../hooks/hotelRoomCategories/useGetHotelRoomCategoriesForSelect";
import useGetHotelExtrasForSelect from "../../../hooks/hotelExtras/useGetHotelExtrasForSelect";
import { createHotelRoomCategory } from "../../../api/apiHotelRoomCategories";
import { EMPTY_HOTEL_TEMPLATE, PRODUCT_TYPE_CODES } from "./helpers/formConstants";
import CoverageBadge from "./CoverageBadge";
import RoomAssignmentCard from "./RoomAssignmentCard";
import AddAssignmentDialog from "./AddAssignmentDialog";

const generateRoomStays = (checkIn, checkOut, existingStays) => {
    if (!checkIn || !checkOut) return [];
    const dates = [];
    const current = new Date(checkIn);
    const end = new Date(checkOut);
    while (current < end) {
        const dateStr = current.toISOString().split('T')[0];
        const existing = existingStays?.find(s => s.date === dateStr);
        dates.push(existing || {
            id: `rs_${Date.now()}_${Math.random()}`,
            date: dateStr,
            room_category_id: null,
            rooms_count: 1,
            base_sell_price: 0,
            base_cost_price: 0,
            custom_sell_price: 0,
            custom_cost_price: 0,
            extras: [],
        });
        current.setDate(current.getDate() + 1);
    }
    return dates;
};

const transformOptions = (items) => {
    if (!items) return [];
    return items.map(item => ({
        id: item.value ?? item.id,
        name: item.label ?? item.name,
    }));
};

const getNextDay = (date) => {
    const d = new Date(date);
    d.setDate(d.getDate() + 1);
    return d.toISOString().split('T')[0];
};

const getDatesInRange = (from, to) => {
    const dates = [];
    const current = new Date(from);
    const end = new Date(to);
    while (current < end) {
        dates.push(current.toISOString().split('T')[0]);
        current.setDate(current.getDate() + 1);
    }
    return dates;
};

const groupRoomAssignments = (room_stays) => {
    if (!room_stays?.length) return [];
    
    // 1. Group stays by their signature (category, count, prices)
    const buckets = {};
    room_stays.forEach(rs => {
        const signature = `${rs.room_category_id ?? ''}_${rs.rooms_count}_${rs.custom_sell_price}_${rs.custom_cost_price}`;
        if (!buckets[signature]) buckets[signature] = [];
        buckets[signature].push(rs);
    });

    const groups = [];

    // 2. For each bucket, extract consecutive sequences
    Object.values(buckets).forEach(bucketStays => {
        // Sort by date
        bucketStays.sort((a, b) => a.date.localeCompare(b.date));

        // We will pull stays out of bucketStays to form groups
        while (bucketStays.length > 0) {
            // Start a new group with the earliest available stay
            const firstStay = bucketStays.shift();
            const currentGroupStays = [firstStay];
            let expectedNextDate = getNextDay(firstStay.date);

            // Greedily find the next consecutive day
            for (let i = 0; i < bucketStays.length; i++) {
                if (bucketStays[i].date === expectedNextDate) {
                    currentGroupStays.push(bucketStays[i]);
                    expectedNextDate = getNextDay(bucketStays[i].date);
                    bucketStays.splice(i, 1); // Remove from pool
                    i--; // Adjust index after splice
                }
            }

            // Create the group object
            const lastStay = currentGroupStays[currentGroupStays.length - 1];
            groups.push({
                id: `grp_${currentGroupStays[0].id}`,
                stays: currentGroupStays,
                from_date: currentGroupStays[0].date,
                to_date: getNextDay(lastStay.date),
                nights: currentGroupStays.length,
                room_category_id: currentGroupStays[0].room_category_id,
                rooms_count: currentGroupStays[0].rooms_count,
                custom_sell_price: currentGroupStays[0].custom_sell_price,
                custom_cost_price: currentGroupStays[0].custom_cost_price,
                extras: currentGroupStays[0].extras || [],
            });
        }
    });

    // 3. Sort final groups by from_date
    groups.sort((a, b) => a.from_date.localeCompare(b.from_date));
    return groups;
};

const getFieldError = (errors, hotelId, field) => {
    return errors && errors[`hotel_${hotelId}_${field}`];
};

const Step1Hotels = ({ formData, setFormData, errors, isEdit, currentData, activeLang, isArabic }) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";

    const startDate = formData.start_date;
    const endDate = formData.end_date;

    const [hotels, setHotels] = useState(() => formData.hotels?.length > 0 ? formData.hotels : []);

    useEffect(() => {
        const syncedHotels = hotels.map(hotel => {
            if (!hotel.room_stays?.length) return hotel;
            const extraMap = new Map();
            hotel.room_stays.forEach(rs => {
                (rs.extras || []).forEach(ex => {
                    const key = `${ex.hotel_extra_id}|${ex.pricing_basis}|${ex.custom_sell_price}|${ex.custom_cost_price}`;
                    if (extraMap.has(key)) {
                        const existing = extraMap.get(key);
                        if (!existing.applicable_dates.includes(rs.date)) {
                            existing.applicable_dates.push(rs.date);
                        }
                    } else {
                        extraMap.set(key, {
                            ...ex,
                            applicable_dates: ex.applicable_dates?.length
                                ? [...ex.applicable_dates]
                                : [rs.date],
                        });
                    }
                });
            });
            return { ...hotel, extras: Array.from(extraMap.values()) };
        });
        setFormData(prev => ({ ...prev, hotels: syncedHotels }));
    }, [hotels, setFormData]);

    const hotelProductTypeCode = PRODUCT_TYPE_CODES.HOTEL;
    const { products: allProducts, isLoading: loadingProducts } = useGetProductsForProgram(1, 100, {});
    const { options: countries, isLoading: loadingCountries } = useGetCountriesForSelect();
    const { options: mealPlans, isLoading: loadingMealPlans } = useGetHotelMealPlansForSelect();
    const { options: ratings, isLoading: loadingRatings } = useGetHotelRatingsForSelect();
    const { options: roomCategories, isLoading: loadingRoomCategories } = useGetHotelRoomCategoriesForSelect();
    const { options: extraOptions } = useGetHotelExtrasForSelect();

    const queryClient = useQueryClient();

    const [creatingCategory, setCreatingCategory] = useState(null);

    const createRoomCategoryMutation = useMutation({
        mutationFn: (nameObj) => createHotelRoomCategory({ name: nameObj, is_active: true }),
    });

    const hotelProduct = useMemo(() => {
        if (!allProducts || !Array.isArray(allProducts)) return null;
        return allProducts.find(p =>
            p.productType?.code === hotelProductTypeCode ||
            p.product_type?.code === hotelProductTypeCode
        );
    }, [allProducts, hotelProductTypeCode]);

    const countriesOptions = useMemo(() => transformOptions(countries), [countries]);
    const mealPlansOptions = useMemo(() => transformOptions(mealPlans), [mealPlans]);
    const ratingsOptions = useMemo(() => transformOptions(ratings), [ratings]);
    const roomCategoriesOptions = useMemo(() => transformOptions(roomCategories), [roomCategories]);

    const addHotelStay = useCallback(() => {
        const newId = `hotel_${Date.now()}_${hotels.length + 1}`;
        const lastHotel = hotels[hotels.length - 1];
        const defaultCheckIn = lastHotel ? lastHotel.check_out_date : startDate;
        setHotels(prev => [...prev, {
            ...EMPTY_HOTEL_TEMPLATE,
            id: newId,
            check_in_date: defaultCheckIn || '',
            product_snapshot: hotelProduct ? {
                product_id: hotelProduct.id,
                title: hotelProduct.title,
                pricing_unit: hotelProduct.pricing_code || hotelProduct.pricing_unit?.code || 'per_person_night',
                pax_type_prices: hotelProduct.pax_type_prices || [],
            } : null,
            selected_supplier: hotelProduct?.suppliers?.[0] || null,
        }]);
    }, [hotels, startDate, hotelProduct]);

    const removeHotelStay = useCallback((id) => {
        setHotels(prev => prev.filter(h => h.id !== id));
    }, []);

    const updateHotelField = useCallback((id, field, value) => {
        setHotels(prev => prev.map(h => {
            if (h.id !== id) return h;
            const updated = { ...h, [field]: value, _ts: Date.now() };
            if (field === 'check_in_date' || field === 'check_out_date') {
                const checkIn = field === 'check_in_date' ? value : h.check_in_date;
                const checkOut = field === 'check_out_date' ? value : h.check_out_date;
                const date1 = checkIn ? new Date(checkIn) : null;
                const date2 = checkOut ? new Date(checkOut) : null;
                if (date1 && date2 && date2 > date1) {
                    const diff = Math.ceil((date2 - date1) / (1000 * 60 * 60 * 24));
                    updated.nights = diff;
                    // Auto-generate room_stays when dates change (new hotels only)
                    updated.room_stays = generateRoomStays(checkIn, checkOut, h.room_stays);
                } else {
                    updated.nights = 0;
                    updated.room_stays = [];
                }
            }
            return updated;
        }));
    }, []);

    const updateRoomStay = useCallback((hotelId, roomStayId, field, value) => {
        setHotels(prev => prev.map(h => {
            if (h.id !== hotelId) return h;
            return {
                ...h,
                room_stays: h.room_stays.map(rs =>
                    rs.id === roomStayId ? { ...rs, [field]: value } : rs
                ),
            };
        }));
    }, []);

    const handleCreateRoomCategory = useCallback(async (hotelId, roomStayId, inputValue) => {
        if (!inputValue.trim() || creatingCategory) return;
        const nameObj = { ar: inputValue, en: inputValue };
        setCreatingCategory(`${hotelId}_${roomStayId}`);
        try {
            const result = await createRoomCategoryMutation.mutateAsync(nameObj);
            const newItem = result?.data;
            if (newItem?.id) {
                const cacheKey = ["HotelRoomCategoriesForSelect", i18next.language];
                queryClient.setQueryData(cacheKey, (old) => ({
                    ...old,
                    data: [...(old?.data || []), newItem]
                }));
                const newCategoryId = String(newItem.id);
                if (roomStayId && roomStayId !== 'legacy') {
                    updateRoomStay(hotelId, roomStayId, 'room_category_id', newCategoryId);
                } else {
                    updateHotelField(hotelId, 'room_category_id', newCategoryId);
                }
            }
        } catch (err) {
            console.error("Failed to create room category:", err);
        } finally {
            setCreatingCategory(null);
        }
    }, [queryClient, createRoomCategoryMutation, creatingCategory, updateRoomStay, updateHotelField]);

    const updateRoomStayExtra = useCallback((hotelId, rsId, extraId, field, value) => {
        setHotels(prev => prev.map(h => {
            if (h.id !== hotelId) return h;
            return {
                ...h,
                room_stays: h.room_stays.map(rs => {
                    if (rs.id !== rsId) return rs;
                    return {
                        ...rs,
                        extras: (rs.extras || []).map(ex =>
                            ex.id === extraId ? { ...ex, [field]: value } : ex
                        ),
                    };
                }),
            };
        }));
    }, []);

    const removeRoomStayExtra = useCallback((hotelId, rsId, extraId) => {
        setHotels(prev => prev.map(h => {
            if (h.id !== hotelId) return h;
            return {
                ...h,
                room_stays: h.room_stays.map(rs => {
                    if (rs.id !== rsId) return rs;
                    return { ...rs, extras: (rs.extras || []).filter(ex => ex.id !== extraId) };
                }),
            };
        }));
    }, []);

    // ── Room Assignment state & handlers ──────────────────────────────────
    const [showAddDialog, setShowAddDialog] = useState(null);

    const addRoomAssignment = useCallback((hotelId, { from_date, to_date, room_category_id, rooms_count, custom_sell_price, custom_cost_price }) => {
        const dates = getDatesInRange(from_date, to_date);
        const newStays = dates.map(date => ({
            id: `rs_${Date.now()}_${Math.random()}`,
            date,
            room_category_id: room_category_id || null,
            rooms_count: rooms_count || 1,
            base_sell_price: 0,
            base_cost_price: 0,
            custom_sell_price: custom_sell_price ?? 0,
            custom_cost_price: custom_cost_price ?? 0,
            extras: [],
        }));
        setHotels(prev => prev.map(h => {
            if (h.id !== hotelId) return h;
            return { ...h, room_stays: [...h.room_stays, ...newStays] };
        }));
    }, []);

    const removeRoomAssignment = useCallback((hotelId, assignment) => {
        const idsToRemove = new Set(assignment.stays.map(rs => rs.id));
        setHotels(prev => prev.map(h => {
            if (h.id !== hotelId) return h;
            return { ...h, room_stays: h.room_stays.filter(rs => !idsToRemove.has(rs.id)) };
        }));
    }, []);

    const updateAssignmentDates = useCallback((hotelId, assignment, newFromDate, newToDate) => {
        if (!newFromDate || !newToDate) return;
        if (new Date(newFromDate) >= new Date(newToDate)) return;

        const newDateRange = getDatesInRange(newFromDate, newToDate);
        const newDateSet = new Set(newDateRange);
        
        const oldDateRange = assignment.stays.map(rs => rs.date);
        const oldDateSet = new Set(oldDateRange);

        setHotels(prev => prev.map(h => {
            if (h.id !== hotelId) return h;
            
            const existingDates = new Set(h.room_stays.map(rs => rs.date));
            const missingDates = newDateRange.filter(date => !existingDates.has(date));
            const newStays = missingDates.map(date => ({
                id: `rs_${Date.now()}_${Math.random()}`,
                date,
                room_category_id: null,
                rooms_count: assignment.rooms_count || 1,
                base_sell_price: 0,
                base_cost_price: 0,
                custom_sell_price: 0,
                custom_cost_price: 0,
                extras: [],
            }));
            const allRoomStays = [...h.room_stays, ...newStays];
            
            const updatedRoomStays = allRoomStays.map(rs => {
                const isNowInGroup = newDateSet.has(rs.date);
                const wasInGroup = oldDateSet.has(rs.date);
                
                if (isNowInGroup) {
                    // Shallow copy extras but create new unique IDs
                    const newExtras = (assignment.extras || []).map(ex => ({...ex, id: `ext_${Date.now()}_${Math.random()}`}));
                    return {
                        ...rs,
                        room_category_id: assignment.room_category_id,
                        rooms_count: assignment.rooms_count,
                        custom_sell_price: assignment.custom_sell_price,
                        custom_cost_price: assignment.custom_cost_price,
                        extras: newExtras
                    };
                } else if (wasInGroup && !isNowInGroup) {
                    return {
                        ...rs,
                        room_category_id: null,
                        custom_sell_price: 0,
                        custom_cost_price: 0,
                        extras: []
                    };
                }
                return rs;
            });
            
            return { ...h, room_stays: updatedRoomStays };
        }));
    }, []);

    const addExtraToAllStays = useCallback((hotelId, assignment) => {
        const newExtra = {
            id: `ext_${Date.now()}_${Math.random()}`,
            hotel_extra_id: null,
            pricing_basis: 'once',
            quantity: 1,
            custom_sell_price: 0,
            custom_cost_price: 0,
        };
        setHotels(prev => prev.map(h => {
            if (h.id !== hotelId) return h;
            const ids = new Set(assignment.stays.map(rs => rs.id));
            return {
                ...h,
                room_stays: h.room_stays.map(rs =>
                    ids.has(rs.id) ? { ...rs, extras: [...(rs.extras || []), { ...newExtra, id: `ext_${Date.now()}_${Math.random()}` }] } : rs
                ),
            };
        }));
    }, []);

    const handleCreateCategoryForAssignment = useCallback(async (hotelId, contextId, inputValue) => {
        if (!inputValue.trim() || creatingCategory) return;
        const nameObj = { ar: inputValue, en: inputValue };
        setCreatingCategory(`${hotelId}_${contextId}`);
        try {
            const result = await createRoomCategoryMutation.mutateAsync(nameObj);
            const newItem = result?.data;
            if (newItem?.id) {
                const cacheKey = ["HotelRoomCategoriesForSelect", i18next.language];
                queryClient.setQueryData(cacheKey, (old) => ({
                    ...old,
                    data: [...(old?.data || []), newItem]
                }));
                const newId = String(newItem.id);
                setHotels(prev => prev.map(h => {
                    if (h.id !== hotelId) return h;
                    const assignments = groupRoomAssignments(h.room_stays);
                    const target = assignments.find(a => a.id === contextId);
                    if (target) {
                        target.stays.forEach(rs => updateRoomStay(hotelId, rs.id, 'room_category_id', newId));
                    }
                    return h;
                }));
            }
        } catch (err) {
            console.error("Failed to create room category:", err);
        } finally {
            setCreatingCategory(null);
        }
    }, [queryClient, createRoomCategoryMutation, creatingCategory, updateRoomStay]);

    const isLoading = loadingProducts || loadingCountries || loadingMealPlans || loadingRatings || loadingRoomCategories;

    const extraOptionsForSelect = useMemo(() => transformOptions(extraOptions), [extraOptions]);

    if (isLoading) {
        return (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
                <CircularProgress />
            </Paper>
        );
    }

    if (!hotelProduct && hotels.length > 0) {
        return (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
                <Alert severity="warning">
                    {isArabic
                        ? 'لم يتم العثور على منتج فندق. الرجاء إنشاء منتج من نوع فندق أولاً.'
                        : 'No hotel product found. Please create a hotel product first.'}
                </Alert>
            </Paper>
        );
    }

    const isNewHotel = (hotel) => hotel.room_stays?.length > 0;

    return (
        <Paper
            sx={{
                p: 3,
                direction: isArabic ? 'rtl' : 'ltr',
                bgcolor: isDark ? 'transparent' : '#FFFFFF',
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                <HotelIcon color="primary" />
                <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" fontWeight={700}>
                        {isArabic ? 'إدارة الفنادق' : 'Hotel Management'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {isArabic
                            ? `المنتج: ${hotelProduct?.title?.ar || hotelProduct?.title?.en || ''}`
                            : `Product: ${hotelProduct?.title?.en || hotelProduct?.title?.ar || ''}`}
                        {hotelProduct?.suppliers?.[0]?.name && (
                            <> — {isArabic ? 'المورد' : 'Supplier'}: {hotelProduct.suppliers[0].name?.en || hotelProduct.suppliers[0].name}</>
                        )}
                    </Typography>
                </Box>
            </Box>

            {hotels.length === 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    {isArabic
                        ? 'لم تقم بإضافة أي فندق بعد. يمكنك إضافة فندق واحد أو أكثر.'
                        : 'No hotels added yet. You can add one or more hotel stays.'}
                </Alert>
            )}

            {hotels.map((hotel, index) => {
                const prevHotel = hotels[index - 1];
                const minCheckIn = prevHotel ? prevHotel.check_out_date : startDate;
                const isNew = isNewHotel(hotel);
                const hasError = errors && Object.keys(errors).some(key => key.startsWith(`hotel_${hotel.id}_`));

                return (
                    <Paper
                        key={hotel.id}
                        variant="outlined"
                        sx={{ 
                            p: 2, 
                            mb: 2, 
                            borderColor: hasError ? 'error.main' : alpha(theme.palette.primary.main, 0.2),
                            borderWidth: hasError ? 2 : 1
                        }}
                    >
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                            <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
                                {isArabic ? `إقامة فندقية ${index + 1}` : `Hotel Stay ${index + 1}`}
                                {hotel.nights > 0 && (
                                    <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                                        ({hotel.nights} {isArabic ? 'ليالي' : 'nights'})
                                    </Typography>
                                )}
                            </Typography>
                            <IconButton size="small" color="error" onClick={() => removeHotelStay(hotel.id)}>
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Stack>

                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <FormInput
                                    label={isArabic ? 'تاريخ الدخول' : 'Check-in Date'}
                                    type="date"
                                    name={`check_in_date_${hotel.id}`}
                                    value={hotel.check_in_date}
                                    onChange={(e) => updateHotelField(hotel.id, 'check_in_date', e.target.value)}
                                    min={minCheckIn}
                                    max={endDate || undefined}
                                    error={!!getFieldError(errors, hotel.id, 'check_in_date')}
                                    helperText={getFieldError(errors, hotel.id, 'check_in_date') || ''}
                                    required
                                />
                                <Box sx={{ mt: 1 }}>
                                    <FormInput
                                        label={isArabic ? 'وقت الدخول' : 'Check-in Time'}
                                        type="time"
                                        name={`check_in_time_${hotel.id}`}
                                        value={hotel.check_in_time}
                                        onChange={(e) => updateHotelField(hotel.id, 'check_in_time', e.target.value)}
                                        error={!!getFieldError(errors, hotel.id, 'check_in_time')}
                                        helperText={getFieldError(errors, hotel.id, 'check_in_time') || ''}
                                        required
                                    />
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormInput
                                    label={isArabic ? 'تاريخ الخروج' : 'Check-out Date'}
                                    type="date"
                                    name={`check_out_date_${hotel.id}`}
                                    value={hotel.check_out_date}
                                    onChange={(e) => updateHotelField(hotel.id, 'check_out_date', e.target.value)}
                                    min={hotel.check_in_date || minCheckIn}
                                    max={endDate || undefined}
                                    error={!!getFieldError(errors, hotel.id, 'check_out_date')}
                                    helperText={getFieldError(errors, hotel.id, 'check_out_date') || ''}
                                    required
                                />
                                <Box sx={{ mt: 1 }}>
                                    <FormInput
                                        label={isArabic ? 'وقت الخروج' : 'Check-out Time'}
                                        type="time"
                                        name={`check_out_time_${hotel.id}`}
                                        value={hotel.check_out_time}
                                        onChange={(e) => updateHotelField(hotel.id, 'check_out_time', e.target.value)}
                                        error={!!getFieldError(errors, hotel.id, 'check_out_time')}
                                        helperText={getFieldError(errors, hotel.id, 'check_out_time') || ''}
                                        required
                                    />
                                </Box>
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <FormInput
                                    label={isArabic ? 'اسم الفندق' : 'Hotel Name'}
                                    type="text"
                                    name={`hotel_name_${hotel.id}`}
                                    value={hotel.hotel_name}
                                    onChange={(e) => {
                                        const val = e.target.value || '';
                                        const titleCased = val.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
                                        updateHotelField(hotel.id, 'hotel_name', titleCased);
                                    }}
                                    error={!!getFieldError(errors, hotel.id, 'hotel_name')}
                                    helperText={getFieldError(errors, hotel.id, 'hotel_name') || ''}
                                    required
                                />
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <FormInput
                                    label={isArabic ? 'الدولة' : 'Country'}
                                    type="select"
                                    name={`country_id_${hotel.id}`}
                                    value={hotel.country_id}
                                    onChange={(e) => {
                                        updateHotelField(hotel.id, 'country_id', e.target.value);
                                        updateHotelField(hotel.id, 'city_id', null);
                                    }}
                                    options={countriesOptions}
                                    error={!!getFieldError(errors, hotel.id, 'country_id')}
                                    helperText={getFieldError(errors, hotel.id, 'country_id') || ''}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <CitySelect
                                    hotelId={hotel.id}
                                    countryId={hotel.country_id}
                                    cityId={hotel.city_id}
                                    updateHotelField={updateHotelField}
                                    isArabic={isArabic}
                                    error={getFieldError(errors, hotel.id, 'city_id')}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <FormInput
                                    label={isArabic ? 'نوع الوجبات' : 'Meal Plan'}
                                    type="select"
                                    name={`meal_plan_id_${hotel.id}`}
                                    value={hotel.meal_plan_id}
                                    onChange={(e) => updateHotelField(hotel.id, 'meal_plan_id', e.target.value)}
                                    options={mealPlansOptions}
                                    error={!!getFieldError(errors, hotel.id, 'meal_plan_id')}
                                    helperText={getFieldError(errors, hotel.id, 'meal_plan_id') || ''}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormInput
                                    label={isArabic ? 'تصنيف الفندق' : 'Hotel Rating'}
                                    type="select"
                                    name={`hotel_rating_id_${hotel.id}`}
                                    value={hotel.hotel_rating_id}
                                    onChange={(e) => updateHotelField(hotel.id, 'hotel_rating_id', e.target.value)}
                                    options={ratingsOptions}
                                    error={!!getFieldError(errors, hotel.id, 'hotel_rating_id')}
                                    helperText={getFieldError(errors, hotel.id, 'hotel_rating_id') || ''}
                                    required
                                />
                            </Grid>
                        </Grid>

                        {/* ─── NEW HOTEL: Room Assignments ─── */}
                        {isNew && (
                            <Box sx={{ mt: 2 }}>
                                <Divider sx={{ mb: 1.5 }} />
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                    <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.85rem' }}>
                                        {isArabic ? 'توزيع الغرف' : 'Room Assignments'}
                                    </Typography>
                                    <CoverageBadge hotel={hotel} isArabic={isArabic} />
                                </Box>

                                {(() => {
                                    const assignments = groupRoomAssignments(hotel.room_stays);
                                    return assignments.length === 0 ? (
                                        <Alert severity="info" sx={{ mb: 1, py: 0.5 }}>
                                            <Typography variant="caption">
                                                {isArabic
                                                    ? 'لم يتم إضافة أي توزيع غرف بعد. استخدم الزر أدناه لإضافة واحد.'
                                                    : 'No room assignments added yet. Use the button below to add one.'}
                                            </Typography>
                                        </Alert>
                                    ) : (
                                        assignments.map((assignment) => (
                                            <RoomAssignmentCard
                                                key={assignment.id}
                                                assignment={assignment}
                                                hotelId={hotel.id}
                                                extraOptions={extraOptions}
                                                extraOptionsForSelect={extraOptionsForSelect}
                                                roomCategoriesOptions={roomCategoriesOptions}
                                                creatingCategory={creatingCategory}
                                                isArabic={isArabic}
                                                onUpdateRoomStay={updateRoomStay}
                                                onUpdateRoomStayExtra={updateRoomStayExtra}
                                                onRemoveRoomStayExtra={removeRoomStayExtra}
                                                onRemoveRoomAssignment={removeRoomAssignment}
                                                onUpdateAssignmentDates={updateAssignmentDates}
                                                onAddExtraToAllStays={addExtraToAllStays}
                                                onCreateCategory={handleCreateCategoryForAssignment}
                                                errors={errors}
                                                hotel={hotel}
                                            />
                                        ))
                                    );
                                })()}

                                {/* Add Room Assignment */}
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<AddIcon />}
                                    onClick={() => setShowAddDialog(hotel.id)}
                                    sx={{ mt: 0.5 }}
                                >
                                    {isArabic ? 'إضافة توزيع غرف' : 'Add Room Assignment'}
                                </Button>

                                {/* Add Assignment Dialog */}
                                <AddAssignmentDialog
                                    open={showAddDialog === hotel.id}
                                    onClose={() => setShowAddDialog(null)}
                                    hotel={hotel}
                                    onAdd={(assignData) => addRoomAssignment(hotel.id, assignData)}
                                    isArabic={isArabic}
                                />
                            </Box>
                        )}

                        {/* ─── LEGACY HOTEL: rooms + room_category_id ─── */}
                        {!isNew && hotel.check_in_date && hotel.check_out_date && (
                            <Grid container spacing={2} sx={{ mt: 1 }}>
                                <Grid item xs={12} md={4}>
                                    <FormInput
                                        label={isArabic ? 'عدد الغرف' : 'Rooms Count'}
                                        type="number"
                                        name={`rooms_${hotel.id}`}
                                        value={hotel.rooms}
                                        onChange={(e) => {
                                            const val = parseInt(e.target.value) || 1;
                                            updateHotelField(hotel.id, 'rooms', Math.max(1, val));
                                        }}
                                        error={!!getFieldError(errors, hotel.id, 'rooms')}
                                        helperText={getFieldError(errors, hotel.id, 'rooms') || ''}
                                        inputProps={{ min: 1 }}
                                        required
                                    />
                                </Grid>
                                <Grid item xs={12} md={8}>
                                    <FormInput
                                        label={isArabic ? 'فئة الغرفة' : 'Room Category'}
                                        type="select"
                                        name={`room_category_id_${hotel.id}`}
                                        value={hotel.room_category_id}
                                        onChange={(e) => updateHotelField(hotel.id, 'room_category_id', e.target.value)}
                                        options={roomCategoriesOptions}
                                        error={!!getFieldError(errors, hotel.id, 'room_category_id')}
                                        helperText={getFieldError(errors, hotel.id, 'room_category_id') || ''}
                                        required
                                        creatable
                                        disabled={creatingCategory !== null}
                                        loading={creatingCategory === `${hotel.id}_legacy`}
                                        onCreate={(inputValue) => handleCreateRoomCategory(hotel.id, 'legacy', inputValue)}
                                    />
                                </Grid>
                            </Grid>
                        )}


                    </Paper>
                );
            })}

            <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={addHotelStay}
                sx={{ mb: 2 }}
            >
                {isArabic ? 'إضافة إقامة فندقية' : 'Add Hotel Stay'}
            </Button>
        </Paper>
    );
};

const CitySelect = ({ hotelId, countryId, cityId, updateHotelField, isArabic, error }) => {
    const { options: cities, isLoading: loadingCities } = useGetCitiesForSelect(countryId);
    const citiesOptions = useMemo(() => transformOptions(cities), [cities]);

    return (
        <FormInput
            label={isArabic ? 'المدينة' : 'City'}
            type="select"
            name={`city_id_${hotelId}`}
            value={cityId}
            onChange={(e) => updateHotelField(hotelId, 'city_id', e.target.value)}
            options={citiesOptions}
            loading={loadingCities}
            error={!!error}
            helperText={error || ''}
        />
    );
};

export default Step1Hotels;
